import {
  Column,
  ColumnOptions as GrecoColumnOptions,
  ThemeProvider,
} from "library/greco-components";
import React, { useMemo, useRef } from "react";
import { useTranslation } from "react-i18next";

import { useGetTaxonomies } from "state/queries/useGetTaxonomies";
import { Taxonomy } from "library/types";
import { ExpandedColumn } from "library/types/columns";
import { useMainContext } from "providers/MainProvider";
import { defaultCols } from "../columns";
import { getColumnGroupings } from "../columnGroupings";
import { Setting } from "library/types/types";
import { APP_ID } from "library/constants";
import { useLoadOffers } from "store/ProposalsPageHooks";
import { useAppTheme } from "hooks/useAppTheme";
import {
  areColumnsEquivalent,
  setVisibility,
} from "library/utils/columnOptionsState";

const reorderColumn = (
  columns: ExpandedColumn[] | undefined,
  index1: number,
  index2: number
) => {
  if (columns && columns.length > 0) {
    const clone = [...columns];
    const [movedItem] = clone.splice(index1, 1);
    if (movedItem) {
      clone.splice(index2, 0, movedItem);
    }
    return clone;
  }
};

export const ColumnOptions = ({
  isOpen,
  closePanel,
}: {
  isOpen: boolean;
  closePanel: () => void;
}) => {
  // Don't fetch - just use cached data from App.tsx
  const { data: taxonomies } = useGetTaxonomies(false);
  const { t } = useTranslation();
  const loadOffers = useLoadOffers();
  // Lets us close the create/edit view dialog once the save succeeded.
  const columnOptionsRef = useRef<{ closeModal: () => void }>(null);

  // Transform taxonomies to { [key: string]: Taxonomy[] }
  const transformedTaxonomies = useMemo(() => {
    if (!taxonomies) return {};
    return Object.entries(taxonomies).reduce((acc, [key, value]) => {
      acc[key] = Array.isArray(value) ? value : [];
      return acc;
    }, {} as { [key: string]: Taxonomy[] });
  }, [taxonomies]);

  const {
    selectedViewColumns,
    onSetSelectedViewId,
    selectedView,
    tempColumns,
    onSetTempColumns,
    onUpdateTempColumns,
    applyDraft,
    discardDraft,
    loadStatus,
    columnOptionsSettings,
    createSetting,
    deleteSetting,
    updateSetting,
  } = useMainContext();

  const areSettingsLoading = loadStatus === "loading";
  const isCreateSettingLoading = loadStatus === "loading";
  const isDeleteSettingLoading = loadStatus === "loading";
  const isUpdateSettingLoading = loadStatus === "loading";
  // A failed view request must not lock the panel: only an ongoing one blocks it.
  const areSettingsLoaded = loadStatus !== "loading";

  const isViewLoading =
    areSettingsLoading ||
    isCreateSettingLoading ||
    isDeleteSettingLoading ||
    isUpdateSettingLoading;

  const transformedColumns = useMemo(() => {
    return tempColumns?.map((c) => {
      if (c.key === "options" || c.key === "SelectedInsurer") {
        return {
          ...c,
          label: t(c.labelKey),
          isPresentInColumnOptions: false,
        };
      }
      return {
        ...c,
        label: t(c.labelKey),
      };
    });
  }, [tempColumns, t]);

  const groupings = useMemo(() => getColumnGroupings(t), [t]);

  const hasUnsavedChanges = useMemo(
    () =>
      !!selectedView && !areColumnsEquivalent(tempColumns, selectedViewColumns),
    [selectedView, tempColumns, selectedViewColumns]
  );

  const theme = useAppTheme();

  if (!isOpen) return null;

  const closeViewDialog = () => columnOptionsRef.current?.closeModal();

  return (
    <ThemeProvider isDarkMode={theme.isDark}>
      <div>
        <GrecoColumnOptions
          t={t as any}
          columns={transformedColumns as Column[]}
          groupings={groupings}
          hasUnsavedChanges={hasUnsavedChanges}
          columnOptionsRef={columnOptionsRef}
          isReady={areSettingsLoaded}
          isViewLoading={isViewLoading}
          closePanel={() => {
            // Nothing reaches the list until "Apply"; closing drops the draft.
            discardDraft();
            closePanel();
          }}
          onChangeView={(id) => {
            onSetSelectedViewId(id);
          }}
          onClickApply={() => {
            const appliedColumns = applyDraft();
            loadOffers({
              skip: 0,
              top: 40,
              reset: true,
              columns: appliedColumns,
              force: true,
            });
            closePanel();
          }}
          onClickClearAllFilters={() => {
            onUpdateTempColumns((columns) =>
              columns.map((c) => ({
                ...c,
                filter: defaultCols.find((dc) => dc.key === c.key)?.filter,
              }))
            );
          }}
          onClickDeleteView={() => {
            if (selectedView) {
              deleteSetting(selectedView.userAppSettingId);
            }
          }}
          onClickReset={() => {
            onSetTempColumns(selectedViewColumns);
          }}
          onClickSaveView={() => {
            if (selectedView) {
              updateSetting({
                ...selectedView,
                userAppSettingValue: JSON.stringify(tempColumns),
              } as Setting);
            }
          }}
          onCreateView={(data) => {
            const newSetting = {
              applicationCodeId: APP_ID,
              appSettingCodeId: 900000000010303,
              userAppSettingValue: JSON.stringify(tempColumns),
              ...data,
            } as Setting;
            createSetting(newSetting, closeViewDialog);
          }}
          onUpdateView={(data) => {
            if (selectedView) {
              updateSetting(
                {
                  ...selectedView,
                  ...data,
                } as Setting,
                closeViewDialog
              );
            }
          }}
          onDeselectAll={() => {
            onUpdateTempColumns((columns) =>
              setVisibility(
                columns,
                columns.map((c) => c.key),
                false
              )
            );
          }}
          onSelectAll={() => {
            onUpdateTempColumns((columns) =>
              setVisibility(
                columns,
                columns.map((c) => c.key),
                true
              )
            );
          }}
          onReorderColumn={(src, dest) => {
            onUpdateTempColumns(
              (columns) => reorderColumn(columns, src, dest) ?? columns
            );
          }}
          onToggleColumnVisibility={(columnKey) => {
            onUpdateTempColumns((columns) => {
              const column = columns.find((c) => c.key === columnKey);
              return setVisibility(columns, [columnKey], !column?.isVisible);
            });
          }}
          onSetColumnsVisibility={(columnKeys, visible) => {
            onUpdateTempColumns((columns) =>
              setVisibility(columns, columnKeys, visible)
            );
          }}
          selectedView={selectedView}
          setFilter={(columnKey, filter) => {
            onUpdateTempColumns((columns) =>
              columns.map((c) =>
                c.key === columnKey ? ({ ...c, filter } as ExpandedColumn) : c
              )
            );
          }}
          taxonomy={transformedTaxonomies}
          views={columnOptionsSettings}
        />
      </div>
    </ThemeProvider>
  );
};
